import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import apiClient from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { FiAward, FiCalendar, FiUpload, FiUser, FiFileText } from 'react-icons/fi';

const RenewCertificateModal = ({ data, isOpen, onClose, onCertificateUpdated }) => {
    const [formData, setFormData] = useState(data ?? {
        id: '',
        expiry_date: '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { showSuccess, showError } = useToast();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.expiry_date) {
            newErrors.expiry_date = 'Expiry date is required';
        } else {
            const expiryDate = new Date(formData.expiry_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (expiryDate < today) {
                newErrors.expiry_date = 'Expiry date cannot be in the past';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await apiClient.post(`/api/certificates/${formData.id}/renew`, { expiry_date: formData.expiry_date });

            if (response.data.success) {
                showSuccess('Certificate renewed successfully');
                onCertificateUpdated(response.data.data);
                handleClose();
            } else {
                showError(response.data.message || 'Failed to renew certificate');
            }
        } catch (error) {
            // Comprehensive error logging for debugging
            console.error('Error updating certificate:', error);

            // Log detailed AxiosError information
            if (error.isAxiosError) {
                console.error('Axios Error Details:', {
                    message: error.message,
                    name: error.name,
                    code: error.code,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.response?.headers,
                    config: {
                        url: error.config?.url,
                        method: error.config?.method,
                        baseURL: error.config?.baseURL,
                        headers: error.config?.headers,
                        timeout: error.config?.timeout
                    }
                });

                // Log full error stack trace
                console.error('Error Stack Trace:', error.stack);
            }

            // Determine appropriate error message based on status code
            let errorMessage = 'Failed to renew certificate';

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message;

                switch (status) {
                    case 400:
                        errorMessage = serverMessage || 'Invalid certificate data provided. Please check all required fields.';
                        break;
                    case 401:
                        errorMessage = 'Authentication required. Please log in again.';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to renew certificates.';
                        break;
                    case 404:
                        errorMessage = 'Certificate renew endpoint not found.';
                        break;
                    case 413:
                        errorMessage = 'Certificate file is too large. Please choose a smaller file.';
                        break;
                    case 422:
                        errorMessage = serverMessage || 'Invalid certificate data format.';
                        break;
                    case 500:
                        errorMessage = 'Server error occurred. Please try again later.';
                        break;
                    default:
                        errorMessage = serverMessage || `Request failed with status ${status}`;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
                console.error('Network Error - No response received:', error.request);
            } else {
                errorMessage = 'An unexpected error occurred while updating the certificate.';
                console.error('Request Setup Error:', error.message);
            }

            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            id: '',
            expiry_date: ''
        });
        setErrors({});
        onClose();
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Renew Certificate" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Expiry Date */}
                <div>
                    <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-1">
                        <FiCalendar className="inline h-4 w-4 mr-1" />
                        Expiry Date *
                    </label>
                    <input
                        type="date"
                        id="expiry_date"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleInputChange}
                        min={getMinDate()}
                        className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.expiry_date ? 'border-red-300' : 'border-gray-300'
                            }`}
                    />
                    {errors.expiry_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>
                    )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                                Renewing...
                            </>
                        ) : (
                            <>
                                <FiAward className="-ml-1 mr-2 h-4 w-4 inline" />
                                Renew Certificate
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default RenewCertificateModal;